import type { Dispatch, SetStateAction } from 'react';
import type { AddItemVisibility } from 'src/components/add-item/types';
import { useRouter } from 'next/router';
import { Text } from '@savvy-kit/foundations/text';
import { EyeOff } from 'lucide-react';
import AdvisorDashboardCard from 'src/components/advisor-dashboard/views/advisor-dashboard-card';
import { CLIENT_LINKED_PROVIDER_ACCOUNT_SOURCES } from 'src/constants/account-link';
import { useFlagsAndLoadingState } from 'src/contexts/launch-darkly-flags-ready-context';
import { graphql } from 'src/gql';
import { usePreviousNonNull } from 'src/hooks/use-previous-non-null';

import type { ProviderAccountSource } from '@savvy/constants/generated/gql-enums';
import { USER_PROFILE_ADVISOR } from '@savvy/constants/user-profiles';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';
import { XBottomSheet } from '@savvy/deprecated-shared/x-components/x-bottom-sheet';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { useActiveHouseholdId } from '../../hooks/use-active-household-id';
import { useConnectNudgeDismissal } from '../../hooks/use-connect-nudge-dismissal';
import { useDisconnectedBannerDismissal } from '../../hooks/use-disconnected-banner-dismissal';
import AddItemDialog from '../add-item/views/add-item-dialog';
import { AddAccountButton } from './net-worth';
import NetWorthAccounts, { handleItemUpdate } from './net-worth-accounts';
import NetWorthGraphBox from './net-worth-graph-box';
import ConnectAccountsNudgeCard from './views/connect-accounts-nudge-card';
import DisconnectedAccountsBanner, { getDisconnectedProviderAccountIds } from './views/disconnected-accounts-banner';
import MobileConnectAccountPreview from './views/mobile-connect-account-preview';
import { ExternalAccountDisclosure } from './views/net-worth-external-account-disclosure';

export const NetWorthPortfolioQuery = graphql(/* GraphQL */ `
  query NetWorthPortfolio($id: ID!, $portfolioId: ID, $householdId: ID) {
    ...NetWorthAccounts
    portfolio(userId: $id, portfolioId: $portfolioId, householdId: $householdId) {
      id
      clientProfile {
        id
        salesforceContactId
        user {
          id
          role
        }
        providerAccounts {
          id
          providerName
          source
          sourceStatus
        }
      }
      ...NetWorthGraphBox
      netWorth {
        amount
        currencyCode
      }
      portfolioItemGroups {
        accounts {
          __typename
        }
        assets {
          __typename
        }
        liabilities {
          __typename
        }
      }
    }
  }
`);

// Canonical "0 connected accounts" rule: a client only counts as connected once they've linked
// an account themselves (PLAID/MONEYKIT) — ORION is a Savvy-managed provider account we create
// on their behalf, not a client-initiated link. Cross-reference this predicate wherever else
// "does this client have connected accounts" is evaluated (e.g. server-side eligibility checks)
// so the two don't drift.
export function hasZeroConnectedAccounts(
  providerAccounts: readonly { source?: ProviderAccountSource | null }[] | null | undefined,
): boolean {
  return !providerAccounts?.some(
    (providerAccount) =>
      !!providerAccount.source && CLIENT_LINKED_PROVIDER_ACCOUNT_SOURCES.includes(providerAccount.source),
  );
}

// Desktop-only v1: gated on LD readiness (avoid a mount flicker between "off" and "on"), the
// flag, non-advisor viewers (including advisors logged in as a client), and
// hasZeroConnectedAccounts so it never doubles up with the old ConnectAccountPreview.
export function shouldShowConnectAccountsNudgeCard({
  flagsReady,
  flagEnabled,
  isAdvisorViewer,
  isAdvisorLoggedInAsClient,
  zeroConnectedAccounts,
}: {
  readonly flagsReady: boolean;
  readonly flagEnabled: boolean;
  readonly isAdvisorViewer: boolean;
  readonly isAdvisorLoggedInAsClient: boolean;
  readonly zeroConnectedAccounts: boolean;
}): boolean {
  return flagsReady && flagEnabled && !isAdvisorViewer && !isAdvisorLoggedInAsClient && zeroConnectedAccounts;
}

export function shouldShowDisconnectedAccountsBanner({
  flagsReady,
  flagEnabled,
  portfolioLoading,
  isAdvisorViewer,
  isAdvisorLoggedInAsClient,
  hasDisconnectedAccounts,
  isDismissed,
  showConnectAccountsNudgeCard,
}: {
  readonly flagsReady: boolean;
  readonly flagEnabled: boolean;
  readonly portfolioLoading: boolean;
  readonly isAdvisorViewer: boolean;
  readonly isAdvisorLoggedInAsClient: boolean;
  readonly hasDisconnectedAccounts: boolean;
  readonly isDismissed: boolean;
  readonly showConnectAccountsNudgeCard: boolean;
}): boolean {
  return (
    flagsReady &&
    flagEnabled &&
    !portfolioLoading &&
    !isAdvisorViewer &&
    !isAdvisorLoggedInAsClient &&
    hasDisconnectedAccounts &&
    !isDismissed &&
    !showConnectAccountsNudgeCard
  );
}

type NetWorthPortfolioProps = {
  readonly clientUserId: string;
  // The advisor-dashboard household id, already resolved by the caller from
  // salesforceAccountId - overrides the client-portal activeHouseholdId path below
  // for the assignedAdvisor/providerAccounts fields threaded into NetWorthAccounts.
  // Does not affect the portfolio() field's own householdId (see comment below).
  readonly householdId?: string;
  readonly showAddItemDialog: AddItemVisibility | undefined;
  readonly householdName?: string;
  readonly showHiddenClientAssetsInGraph?: boolean;
  readonly setShowAddItemDialog: Dispatch<SetStateAction<AddItemVisibility | undefined>>;
};

function NetWorthPortfolio({
  clientUserId,
  householdId: householdIdProp,
  showAddItemDialog,
  showHiddenClientAssetsInGraph = false,
  setShowAddItemDialog,
  householdName,
}: NetWorthPortfolioProps) {
  const { user } = useAuth();
  // scope the portfolio() field to the viewer's active household (client portal only).
  // Advisor-dash (clientUserId set) keeps using the legacy user_id path.
  const { activeHouseholdId, canQuery } = useActiveHouseholdId();
  const portfolioHouseholdId = clientUserId ? undefined : (activeHouseholdId ?? undefined);
  // Separate from portfolioHouseholdId: NetWorthAccounts's assignedAdvisor/providerAccounts
  // fields should use the caller-supplied household id on the advisor dashboard (where one is
  // already known from salesforceAccountId) rather than sitting unscoped just because
  // portfolio() intentionally ignores activeHouseholdId there.
  const householdId = householdIdProp ?? portfolioHouseholdId;
  const canQueryPortfolio = Boolean(clientUserId) || canQuery;
  const portfolioTargetUserId = (clientUserId || user?.id) ?? '';
  const {
    error: portfolioError,
    data: portfolioData,
    loading: queryLoading,
    refetch: portfolioRefetch,
  } = useQueryWithErrorLogging(NetWorthPortfolioQuery, {
    variables: {
      id: portfolioTargetUserId,
      householdId: portfolioHouseholdId,
    },
    skip: !canQueryPortfolio,
  });
  const portfolioLoading = !canQueryPortfolio || queryLoading;

  // Reset on the query target, or switching between two clients renders the one you left
  // under the name of the one you opened until the new portfolio arrives.
  const previousData = usePreviousNonNull(portfolioData, `${portfolioTargetUserId}:${portfolioHouseholdId ?? ''}`);
  const isSmallScreen = useIsSmallScreen();
  const router = useRouter();
  const { flagsReady, flags } = useFlagsAndLoadingState();
  const isAdvisorLoggedInAsClient = !!loggedInAsAdvisorID();
  const { isDismissed: isConnectAccountsNudgeCardDismissed, dismiss: dismissConnectAccountsNudgeCard } =
    useConnectNudgeDismissal();
  const providerAccounts = (portfolioData ?? previousData)?.portfolio.clientProfile?.providerAccounts;
  const disconnectedIds = getDisconnectedProviderAccountIds(providerAccounts);
  const { isDismissed: isDisconnectedBannerDismissed, dismiss: dismissDisconnectedBanner } =
    useDisconnectedBannerDismissal(disconnectedIds);
  if (user?.role === 'ADVISOR' && portfolioData?.portfolio.clientProfile?.user.role === 'NON_ADVISORY_USER') {
    // This is an IMPORTANT privacy guard. Advisors are not allowed to view
    // non-advisory users' net-worth

    router.push(`/advisor/dashboard/client/${portfolioData.portfolio.clientProfile.salesforceContactId}`);
    return null;
  }
  // We want to render previousData until new data comes in
  const portfolioDataToRender = portfolioData ?? previousData;

  const numberOfAccounts = portfolioDataToRender?.portfolio.portfolioItemGroups.reduce(
    (a, b) => a + (b.accounts.length + b.assets.length + b.liabilities.length),
    0,
  );

  const emptyPortfolio = numberOfAccounts === 0;
  const isAdvisorViewer = user?.profileType === USER_PROFILE_ADVISOR;

  const showConnectAccountsNudgeCard = shouldShowConnectAccountsNudgeCard({
    flagsReady,
    flagEnabled: flags['flagConnectAccountsNudgeCard'] === true,
    isAdvisorViewer,
    isAdvisorLoggedInAsClient,
    // hasZeroConnectedAccounts(undefined) defaults to true -- don't show the card until
    // we actually know the client has zero connected accounts.
    zeroConnectedAccounts: portfolioDataToRender
      ? hasZeroConnectedAccounts(portfolioDataToRender.portfolio.clientProfile?.providerAccounts)
      : false,
  });
  const showDisconnectedBanner = shouldShowDisconnectedAccountsBanner({
    flagsReady,
    flagEnabled: flags['flagDisconnectedAccountsBanner'] === true,
    portfolioLoading,
    isAdvisorViewer,
    isAdvisorLoggedInAsClient,
    hasDisconnectedAccounts: disconnectedIds.length > 0,
    isDismissed: isDisconnectedBannerDismissed,
    showConnectAccountsNudgeCard,
  });

  return (
    <>
      {portfolioError && `Error! ${portfolioError.message}`}
      {showDisconnectedBanner && !isSmallScreen && (
        <DisconnectedAccountsBanner providerAccounts={providerAccounts} onDismiss={dismissDisconnectedBanner} />
      )}
      {!(emptyPortfolio && isSmallScreen) &&
        (!isSmallScreen && showConnectAccountsNudgeCard && !isConnectAccountsNudgeCardDismissed ? (
          <div className="flex flex-row gap-6 items-start">
            <div className="min-w-0 flex-1">
              <NetWorthGraphBox
                value={portfolioDataToRender?.portfolio.netWorth}
                netWorthPortfolioFrag={portfolioDataToRender?.portfolio}
                showHiddenClientAssetsInGraph={showHiddenClientAssetsInGraph}
                emptyPortfolio={emptyPortfolio}
                setShowAddItemDialog={setShowAddItemDialog}
              />
            </div>
            <div className="mt-4 w-[340px] shrink-0">
              <ConnectAccountsNudgeCard
                onConnect={() => {
                  setShowAddItemDialog('VISIBLE');
                }}
                onDismiss={dismissConnectAccountsNudgeCard}
              />
            </div>
          </div>
        ) : (
          <NetWorthGraphBox
            value={portfolioDataToRender?.portfolio.netWorth}
            netWorthPortfolioFrag={portfolioDataToRender?.portfolio}
            showHiddenClientAssetsInGraph={showHiddenClientAssetsInGraph}
            emptyPortfolio={emptyPortfolio}
            setShowAddItemDialog={setShowAddItemDialog}
          />
        ))}
      {(() => {
        if (isSmallScreen) {
          if (emptyPortfolio) {
            return (
              <>
                <MobileConnectAccountPreview setShowAddItemDialog={setShowAddItemDialog} />
                {showAddItemDialog && (
                  <AddItemDialog
                    hiddenFromClient={false}
                    onSuccess={({ id, type }) => {
                      setShowAddItemDialog(undefined);
                      return handleItemUpdate({
                        id,
                        type,
                        portfolioRefetch,
                        router,
                      });
                    }}
                    onClose={() => {
                      setShowAddItemDialog(undefined);
                    }}
                  />
                )}
              </>
            );
          }

          return (
            <XBottomSheet>
              <NetWorthAccounts
                showAddItemDialog={showAddItemDialog === 'VISIBLE'}
                setShowAddItemDialog={setShowAddItemDialog}
                portfolioRefetch={portfolioRefetch}
                queryFrag={portfolioDataToRender}
                clientUserId={clientUserId}
                householdId={householdId}
                hiddenAccountsView={false}
                emptyPortfolio={emptyPortfolio}
              />
              <AddAccountButton setShowAddItemDialog={setShowAddItemDialog} />
              {!portfolioLoading && numberOfAccounts !== 0 && (
                <div className="mt-8">
                  <ExternalAccountDisclosure />
                </div>
              )}
            </XBottomSheet>
          );
        }

        return (
          <NetWorthAccounts
            showAddItemDialog={showAddItemDialog === 'VISIBLE'}
            setShowAddItemDialog={setShowAddItemDialog}
            portfolioRefetch={portfolioRefetch}
            queryFrag={portfolioDataToRender}
            clientUserId={clientUserId}
            householdId={householdId}
            hiddenAccountsView={false}
            emptyPortfolio={emptyPortfolio}
            showConnectAccountsNudgeCard={showConnectAccountsNudgeCard}
          />
        );
      })()}
      {user?.profileType === USER_PROFILE_ADVISOR && (
        <AdvisorDashboardCard className="p-4 mt-8 bg-background-light">
          <div className="mx-4 flex flex-row items-center gap-2">
            <EyeOff className="size-4 text-secondary" />

            <Text color="secondary" variant="overline">{`Not visible to ${
              householdName?.replace(' Household', '') || 'client'
            }`}</Text>
          </div>
          <NetWorthAccounts
            showAddItemDialog={showAddItemDialog === 'HIDDEN_FROM_CLIENT'}
            setShowAddItemDialog={setShowAddItemDialog}
            portfolioRefetch={portfolioRefetch}
            queryFrag={portfolioDataToRender}
            clientUserId={clientUserId}
            householdId={householdId}
            hiddenAccountsView
          />
        </AdvisorDashboardCard>
      )}
      {!portfolioLoading && numberOfAccounts !== 0 && !isSmallScreen && (
        <div className="mt-[92px]">
          <ExternalAccountDisclosure />
        </div>
      )}
    </>
  );
}

export default NetWorthPortfolio;
